/**
 * workflow.service.ts — Layer 3: Workflows Domain
 *
 * The only file allowed to call the low-level HTTP client for the Workflows
 * feature. Components/hooks under `src/workflows/` import exclusively from
 * here (never from `http.service.ts` directly), per the 3-layer API rule.
 */

import { getRequest, postRequest, putRequest, deleteRequest } from './http.service';
import {
  URL_WORKFLOWS,
  URL_WORKFLOWS_TOOLS,
  URL_WORKFLOWS_GENERATE,
  URL_WORKFLOW_BY_ID,
  URL_WORKFLOW_DUPLICATE,
  URL_WORKFLOW_ACTIVATE,
  URL_WORKFLOW_DEACTIVATE,
  URL_WORKFLOW_RUN,
  URL_WORKFLOW_RUNS,
  URL_WORKFLOW_RUN_BY_ID,
  URL_WORKFLOW_RUN_STOP,
  URL_WORKFLOW_RUN_RESUME,
} from './url.service';
import type {
  WorkflowDefinition,
  WorkflowRun,
  WorkflowToolCatalog,
  WorkflowTrigger,
} from '../workflows/workflow.types';

interface ApiSuccess<T> {
  success: true;
  data: T;
}

export const fetchWorkflows = () =>
  getRequest<ApiSuccess<WorkflowDefinition[]>>(URL_WORKFLOWS);

export const fetchWorkflowTools = () =>
  getRequest<ApiSuccess<WorkflowToolCatalog>>(URL_WORKFLOWS_TOOLS);

export const fetchWorkflowById = (id: string) =>
  getRequest<ApiSuccess<WorkflowDefinition>>(URL_WORKFLOW_BY_ID(id));

export const createWorkflow = (payload: {
  name: string;
  description?: string;
  trigger: WorkflowTrigger;
}) => postRequest<ApiSuccess<WorkflowDefinition>>(URL_WORKFLOWS, payload as unknown as Record<string, unknown>);

export const generateWorkflow = (request: string) =>
  postRequest<ApiSuccess<{ workflow: WorkflowDefinition; assumptions: string[] }>>(
    URL_WORKFLOWS_GENERATE,
    { request },
  );

export const updateWorkflow = (
  id: string,
  payload: Partial<Pick<WorkflowDefinition, 'name' | 'description' | 'trigger' | 'nodes' | 'edges'>>,
) =>
  putRequest<ApiSuccess<WorkflowDefinition>>(
    URL_WORKFLOW_BY_ID(id),
    payload as unknown as Record<string, unknown>,
  );

export const deleteWorkflow = (id: string) =>
  deleteRequest<{ success: true }>(URL_WORKFLOW_BY_ID(id));

export const duplicateWorkflow = (id: string) =>
  postRequest<ApiSuccess<WorkflowDefinition>>(URL_WORKFLOW_DUPLICATE(id));

export const activateWorkflow = (id: string) =>
  postRequest<ApiSuccess<WorkflowDefinition>>(URL_WORKFLOW_ACTIVATE(id));

export const deactivateWorkflow = (id: string) =>
  postRequest<ApiSuccess<WorkflowDefinition>>(URL_WORKFLOW_DEACTIVATE(id));

export const runWorkflow = (id: string, triggerContext?: Record<string, unknown>) =>
  postRequest<ApiSuccess<{ runId: string; status: string }>>(
    URL_WORKFLOW_RUN(id),
    triggerContext ? { triggerContext } : undefined,
  );

export const fetchWorkflowRuns = (id: string) =>
  getRequest<ApiSuccess<WorkflowRun[]>>(URL_WORKFLOW_RUNS(id));

export const fetchWorkflowRun = (runId: string) =>
  getRequest<ApiSuccess<WorkflowRun>>(URL_WORKFLOW_RUN_BY_ID(runId));

export const stopWorkflowRun = (runId: string) =>
  postRequest<{ success: true }>(URL_WORKFLOW_RUN_STOP(runId));

export const resumeWorkflowRun = (runId: string, fields: { date: string; startTime: string }) =>
  postRequest<{ success: true }>(URL_WORKFLOW_RUN_RESUME(runId), fields as unknown as Record<string, unknown>);
